import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Loader2, Lock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { fmtDate, formatApiErrorDetail } from "@/components/schulungen/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EinladungEinloesen() {
  const { token } = useParams();
  const { setSession, logout } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Einladung zur Schulung | Lehnen & Weinacht";
    axios.get(`${API}/schulungen/einladung/${token}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
    if (pw !== pw2) return setError("Die Passwörter stimmen nicht überein.");
    setLoading(true);
    try {
      logout();
      const { data } = await axios.post(`${API}/schulungen/einladung/${token}/einloesen`, { passwort: pw }, { withCredentials: true });
      setSession(data);
      navigate("/schulungen/teilnehmer", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, "Einlösen fehlgeschlagen"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="einladung-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-cyan-700 text-white flex items-center justify-center mx-auto mb-4"><GraduationCap className="h-7 w-7" /></div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Einladung zur Schulung</h1>
          <p className="text-sm text-slate-500 mt-1">Basisqualifikation nach § 45b SGB XI</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-8">
          {notFound ? (
            <div className="text-center" data-testid="einladung-invalid">
              <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <p className="font-semibold text-slate-800">Einladung nicht gefunden</p>
              <p className="text-sm text-slate-500 mt-2">Der Link ist ungültig. Bitte wenden Sie sich an Ihren Arbeitgeber, um einen neuen Link zu erhalten.</p>
            </div>
          ) : !info ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div>
          ) : !info.gueltig ? (
            <div className="text-center" data-testid="einladung-invalid">
              <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <p className="font-semibold text-slate-800">{info.status === "eingeloest" ? "Diese Einladung wurde bereits eingelöst" : info.status === "abgelaufen" ? "Diese Einladung ist abgelaufen" : "Diese Einladung ist nicht mehr gültig"}</p>
              <p className="text-sm text-slate-500 mt-2">{info.status === "eingeloest" ? "Sie können sich mit Ihren Zugangsdaten anmelden." : `Bitte ${info.firmenname} um einen neuen Einladungslink.`}</p>
              {info.status === "eingeloest" && <Link to="/schulungen/login" className="inline-block mt-4 text-cyan-700 font-semibold hover:underline" data-testid="einladung-to-login">Zum Login</Link>}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="einladung-form">
              <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-4 text-sm text-slate-700" data-testid="einladung-info">
                <p>Hallo <strong>{info.vorname} {info.nachname}</strong>,</p>
                <p className="mt-1"><strong>{info.firmenname}</strong> hat für Sie einen Schulungsplatz reserviert. Legen Sie jetzt Ihr Passwort fest, um zu starten.</p>
                <p className="mt-2 text-xs text-slate-500">Zugang: {info.email} · Link gültig bis {fmtDate(info.expires_at)}</p>
              </div>
              {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-xl border border-red-200" data-testid="einladung-error">{error}</div>}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Passwort wählen (min. 8 Zeichen)</Label>
                <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="h-12 pl-10 rounded-xl" data-testid="einladung-passwort" /></div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Passwort wiederholen</Label>
                <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-12 pl-10 rounded-xl" data-testid="einladung-passwort2" /></div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold" data-testid="einladung-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-2 h-5 w-5" />Zugang anlegen & starten</>}
              </Button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6"><Link to="/schulungen" className="hover:text-cyan-700">Mehr über die Schulung erfahren</Link></p>
      </div>
    </div>
  );
}
